// Campaign presets for the configurator page.
//
// Each preset returns a full CampaignConfig instance ready to drop into form
// state. Kept as pure data builders so the configurator can re-run them on
// demand (e.g. "reset to preset").

import {
  AxisSpec,
  CampaignConfig,
  CANONICAL_INPUT_DEFAULTS,
  CANONICAL_OUTPUTS,
  CanonicalInputName,
  OutputSpec,
} from '../types';

function rangeAxis(
  name: CanonicalInputName,
  min?: number,
  max?: number,
): AxisSpec {
  const defaults = CANONICAL_INPUT_DEFAULTS[name];
  const [dMin, dMax] = defaults.defaultRange ?? [0, 1];
  return {
    name,
    units: defaults.units,
    mode: 'range',
    min: min ?? dMin,
    max: max ?? dMax,
  };
}

function outputsFrom(
  names: readonly string[],
  weight = 1,
): OutputSpec[] {
  return names.map((name) => ({ name, weight }));
}

const ALL_OUTPUTS = outputsFrom(CANONICAL_OUTPUTS);

function baseConfig(partial: Partial<CampaignConfig>): CampaignConfig {
  return {
    name: 'New campaign',
    inputs: [],
    outputs: [],
    budget: 50,
    batchSize: 4,
    lowFiSolver: 'AVL',
    highFiSolver: 'Flow360',
    computePool: 'default',
    flexCreditsPerHighFiRun: 50,
    stopEarlyIfTargetsMet: true,
    sampler: { engine: 'mock-surrogate' },
    postDivergenceSampling: {
      enabled: false,
      axisName: 'alpha',
      outputName: 'CL',
      minPoints: 6,
      dropFraction: 0.18,
    },
    oracle: { enabled: false },
    ...partial,
  };
}

type Preset = { label: string; build: () => CampaignConfig };

export const PRESETS = {
  tailor_mannequin_stall: {
    label: 'Tailor + Mannequin stall demo',
    build: () =>
      baseConfig({
        name: 'Tailor + Mannequin — post-stall demo',
        inputs: [
          rangeAxis('alpha', -5, 24),
          rangeAxis('beta', -8, 8),
        ],
        outputs: ALL_OUTPUTS,
        budget: 36,
        batchSize: 4,
        sampler: {
          engine: 'tailor-mannequin',
          mannequinFamily: 'cessna-like',
          tailorWeights: {
            value_uncertainty: 0.2,
            gradient: 0.2,
            topology: 0.2,
            downstream: 0.2,
            coverage: 0.2,
          },
        },
        postDivergenceSampling: {
          enabled: true,
          axisName: 'alpha',
          outputName: 'CL',
          minPoints: 6,
          dropFraction: 0.12,
          guardBand: 1.0,
        },
        oracle: { enabled: false },
      }),
  },
  cessna_2d: {
    label: '2D Cessna 182 demo',
    build: () =>
      baseConfig({
        name: 'Cessna 182 — alpha x beta sweep',
        inputs: [
          rangeAxis('alpha', -5, 20),
          rangeAxis('beta', -10, 10),
        ],
        outputs: ALL_OUTPUTS,
        budget: 40,
        batchSize: 5,
        oracle: { enabled: true, datasetId: 'cessna182' },
      }),
  },
  x57_2d: {
    label: '2D X-57 oracle (alpha x RPM, CL+CD)',
    build: () =>
      baseConfig({
        name: 'X-57 Maxwell — 2D (alpha x RPM)',
        inputs: [
          rangeAxis('alpha', -5, 20),
          rangeAxis('RPM', 0, 6000),
        ],
        outputs: outputsFrom(['CL', 'CD']),
        budget: 30,
        batchSize: 6,
        oracle: { enabled: true, datasetId: 'x57-bet-2d' },
      }),
  },
  x57_3d: {
    label: '3D X-57 oracle (alpha x beta x RPM, CL+CD+CMx+CMy+CMz)',
    build: () =>
      baseConfig({
        name: 'X-57 Maxwell — 3D (alpha x beta x RPM)',
        inputs: [
          rangeAxis('alpha', -5, 20),
          rangeAxis('RPM', 0, 6000),
          rangeAxis('beta', -10, 10),
        ],
        outputs: outputsFrom(['CL', 'CD', 'Cmx', 'Cmy', 'Cmz']),
        budget: 60,
        batchSize: 6,
        oracle: { enabled: true, datasetId: 'x57-bet-3d' },
      }),
  },
  generic_6d: {
    label: 'Generic 6D production campaign (no oracle)',
    build: () =>
      baseConfig({
        name: 'Generic 6D production campaign',
        inputs: [
          rangeAxis('alpha'),
          rangeAxis('beta'),
          rangeAxis('Mach'),
          rangeAxis('Re'),
          rangeAxis('RPM'),
          rangeAxis('flap'),
        ],
        outputs: ALL_OUTPUTS,
        budget: 120,
        batchSize: 8,
        oracle: { enabled: false },
      }),
  },
  empty: {
    label: 'Empty',
    build: () =>
      baseConfig({
        name: 'New campaign',
        inputs: [rangeAxis('alpha'), rangeAxis('beta')],
        outputs: ALL_OUTPUTS,
        budget: 50,
        batchSize: 4,
        oracle: { enabled: false },
      }),
  },
} satisfies Record<string, Preset>;

export type PresetKey = keyof typeof PRESETS;
